<template>
  <div class="page-shell">
    <div class="pa-4 pb-12">
      <!-- <VendorDashboardHeader :title="templateName" /> -->
      <div class="justify-space-between">
        <div class="mb-2">
          <h4 class="text-capitalize" style="font-weight: 500">{{ templateName }}</h4>
        </div>
        <AppButtonFlat color="primary" prepend-icon="mdi-plus" @click="handleAddSection">
          Add Section
        </AppButtonFlat>
      </div>

      <div v-for="section in sections" :key="section.id" class="quote-section">
        <div class="mt-3 bg-white rounded-lg">
          <div class="section-top px-2 py-2">
            <div class="section-fields">
              <p class="section-name">{{ section.title }}</p>
              <p v-if="section.description" class="section-description">
                {{ section.description }}
              </p>
            </div>
            <div class="section-actions-right">
              <v-btn
                size="small"
                icon
                color="primary"
                variant="tonal"
                @click="editSection(section.id)"
                aria-label="Edit section"
              >
                <v-icon size="18">mdi-pencil-outline</v-icon>
              </v-btn>
            </div>
          </div>
          <div>
            <div class="px-2">
              <v-btn
                color="primary"
                style="font-weight: 500; text-transform: capitalize !important"
                variant="tonal"
                @click="addItem(section.id)"
              >
                <v-icon>mdi-plus-circle</v-icon>
                Add item
              </v-btn>
              <div class="quote-table-shell my-2">
                <table class="quote-table">
                  <thead>
                    <tr>
                      <th class="quote-col-item">Item</th>
                      <th class="quote-col-total" style="text-align: center;">Qty</th>
                      <th class="quote-col-total">Amount</th>
                      <th class="quote-col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!section.items.length">
                      <td class="quote-empty" colspan="3">
                        No items yet. Click “Add item” to start.
                      </td>
                    </tr>
                    <tr v-for="item in section.items" :key="item.id">
                      <td class="quote-col-item">
                        {{ item.name || item.title || "—" }}
                      </td>
                      <td class="quote-col-total" style="text-align: center;">
                        {{ hasQty(item) ? item.qty : "—" }}
                      </td>
                      <td class="quote-col-total">
                        {{ formatCurrency(lineTotal(item)) }}
                      </td>
                      <td class="quote-col-actions">
                        <div class="row-actions">
                          <v-btn
                            size="small"
                            variant="text"
                            color="primary"
                            icon
                            @click="editItem(section.id, item.id)"
                          >
                            <v-icon size="18">mdi-pencil-outline</v-icon>
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="section.items.length">
                    <tr>
                      <td class="section-total-label" colspan="2">Section Total</td>
                      <td class="section-total text-right" colspan="2">
                        {{ formatCurrency(sectionSubtotal(section)) }}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- summary table right aligned -- sub total, total -->
      <div class="summary-table">
        <table class="summary">
          <tfoot>
            <tr>
              <td class="section-total-label">Subtotal</td>
              <td class="section-total text-right">
                {{ formatCurrency(summary.sub_total) }}
              </td>
            </tr>
            <tr v-if="summary.tax_amount > 0">
              <td class="section-total-label">
                <div class="tax-row-label">
                  <div class="tax-row-left">
                    <span>Tax</span>
                  </div>
                </div>
              </td>
              <td class="section-total text-right">
                {{ formatCurrency(summary.tax_amount) }}
              </td>
            </tr>
            <tr v-if="summary.fee_amount > 0">
              <td class="section-total-label">Fee</td>
              <td class="section-total text-right">
                {{ formatCurrency(summary.fee_amount) }}
              </td>
            </tr>
            <tr>
              <td class="section-total-label">Total</td>
              <td class="section-total text-right">
                {{ formatCurrency(summary.total_amount) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    <div class="btm-fixed">
      <AppButtonFlat
        block
        :disabled="!hasInquiryId || totalAmount == 0"
        color="primary"
        prepend-icon="mdi-check-circle-outline"
        class="mb-2"
        @click="handleUseTemplate()"
      >
        Send Quotation
      </AppButtonFlat>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { formatCurrency } from "@/shared/utils/utils";
import { useModalStore } from "@/shared/stores/modal";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { getQuotationTemplateDetail } from "@/modules/vendor/services/quotation-templates.api";
import { useRoute, useRouter } from "vue-router";
import AddQuotationSectionModal, {
  type QuotationSectionInput,
} from "@/modules/vendor/components/quotation-detail/AddQuotationSectionModal.vue";
import AddQuotationItemModal, {
  type QuotationItemInput,
} from "@/modules/vendor/components/quotation-detail/AddQuotationItemModal.vue";
import UseQuotationTemplateModal from "@/modules/vendor/components/quotation-detail/UseQuotationTemplateModal.vue";
import App from "@/App.vue";

type Id = string | number;

type QuotationItem = {
  id: Id;
  name?: string | null;
  title?: string | null;
  qty?: string | number | "";
  unit?: string | null;
  rate?: string | number | null;
  amount?: string | number | null;
  line_total?: string | number | null;
};

type QuotationSection = {
  id: Id;
  title: string;
  description: string | null;
  items: QuotationItem[];
};

const props = defineProps<{
  id: string | number;
}>();

const modal = useModalStore();
const snackbar = useSnackbarStore();
const route = useRoute();
const router = useRouter();

const hasInquiryId = computed(() => String(route.query?.inquiryId ?? "").trim() !== "");

const template = ref<Record<string, any> | null>(null);
const isLoadingTemplate = ref(false);
const pendingTemplateReload = ref(false);
const apiSummary = ref<Record<string, any> | null>(null);

const templateName = computed(() => template.value?.name || "Template Detail");

const sections = ref<QuotationSection[]>([]);

function genId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function toNumber(value: string | number) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number.parseFloat(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasQty(item: QuotationItem) {
  return String(item.qty ?? "").trim() !== "";
}

function qtyForTotal(item: QuotationItem) {
  return hasQty(item) ? Math.max(toNumber(item.qty as string | number), 0) : 1;
}

function lineTotal(item: QuotationItem) {
  const amount = item.line_total ?? item.amount ?? item.rate ?? 0;
  return qtyForTotal(item) * toNumber(amount as string | number);
}

function sectionSubtotal(section: QuotationSection) {
  return section.items.reduce((sum, item) => sum + lineTotal(item), 0);
}

const summary = computed(() => {
  return {
    sub_total: apiSummary.value?.sub_total??0,

    tax_amount: apiSummary.value?.tax_amount??0,

    fee_amount: apiSummary.value?.fee_amount??0,

    vendor_commission: apiSummary.value?.vendor_commission??0,

    company_fee: apiSummary.value?.company_fee??0,

    total_amount: apiSummary.value?.total_amount??0,
  };
});

const totalAmount = computed(() => toNumber(summary.value.total_amount));

async function loadTemplateDetail(force = false) {
  if (isLoadingTemplate.value) {
    if (force) pendingTemplateReload.value = true;
    return;
  }
  isLoadingTemplate.value = true;

  try {
    const inquiryId = String(route.query.inquiryId || "").trim();
    const response = await getQuotationTemplateDetail(
      props.id,
      inquiryId ? { inquiry_id: inquiryId } : {},
    );


    template.value = (response as any).data;
    apiSummary.value = (response as any).summary;

    console.log("API Summary:", apiSummary.value);

    const apiSections = (response as any).data.sections;
    sections.value = apiSections.map((section: any) => ({
      id: section.id ?? genId(),
      title: section.title ?? section.name ?? "",
      description: section.description ?? null,
      items: Array.isArray(section.items) ? section.items : [],
    }));
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to load template detail.";
    snackbar.error(message);
  } finally {
    isLoadingTemplate.value = false;
    if (pendingTemplateReload.value) {
      pendingTemplateReload.value = false;
      // ensure we refresh even if a request was already in-flight
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      loadTemplateDetail();
    }
  }
}

function addSection(payload: { title: string; description: string }) {
  sections.value.push({
    id: genId(),
    title: payload.title,
    description: payload.description || null,
    items: [],
  });
}

function editSection(sectionId: Id) {
  const section = sections.value.find((s) => s.id === sectionId);
  if (!section) return;

  const onSave = (payload: QuotationSectionInput) => {
    section.title = payload.title;
    section.description = payload.description || null;
  };

  modal.open(
    AddQuotationSectionModal,
    {
      templateId: props.id,
      sectionId,
      mode: "edit",
      initial: { title: section.title, description: section.description ?? "" },
      onSave,
      onDeleted: () => loadTemplateDetail(true),
    },
    { fullscreen: true, showHeader: false, title: "Edit section" },
  );
}

function addItem(sectionId: Id) {
  const section = sections.value.find((s) => s.id === sectionId);
  if (!section) return;

  const onAdd = async (_item: QuotationItemInput) => {
    await loadTemplateDetail(true);
  };

  modal.open(
    AddQuotationItemModal,
    { sectionId, onAdd, mode: "add", initial: { qty: "", unit: "", rate: 0 } },
    {
      fullscreen: true,
      showHeader: false,
      title: "Add item",
    },
  );
}

	function editItem(sectionId: Id, itemId: Id) {
	  const section = sections.value.find((s) => s.id === sectionId);
	  if (!section) return;
	  const item = section.items.find((i) => i.id === itemId);
	  if (!item) return;

	  const onSave = (payload: QuotationItemInput) => {
	    item.title = payload.title;
	    item.qty = payload.qty;
	    item.unit = payload.unit;
	    item.rate = payload.rate;
	  };

	  modal.open(
	    AddQuotationItemModal,
	    {
        sectionId,
        itemId,
	      mode: "edit",
	      initial: { title: item.title ?? item.name ?? "", qty: item.qty, unit: item.unit, rate: item.rate },
	      onSave: () => loadTemplateDetail(true),
        onDeleted: () => loadTemplateDetail(true),
	    },
	    { fullscreen: true, showHeader: false, title: "Edit item" },
	  );
	}


const handleAddSection = () => {
  modal.open(
    AddQuotationSectionModal,
    {
      templateId: props.id,
      onAdd: async (payload: QuotationSectionInput) => {
        addSection(payload);
        await loadTemplateDetail(true);
      },
    },
    {
      fullscreen: true,
      showHeader: false,
      title: "Add Section",
    },
  );
};

function handleUseTemplate() {
  const inquiryId = String(route.query?.inquiryId ?? "").trim();
  if (!inquiryId) {
    snackbar.error("Inquiry ID missing.");
    return;
  }
  modal.open(
    UseQuotationTemplateModal,
    {
      inquiryId,
      quotationTemplateId: props.id,
      summary: summary.value,
      onSuccess: () => {
        router.push({ name: "vendor.inquiry.detail", params: { id: inquiryId } });
      },
    },
    {
      fullscreen: false,
      showHeader: true,
      title: "Send quotation",
    },
  );
}

onMounted(loadTemplateDetail);
watch(() => props.id, () => loadTemplateDetail());
</script>

<style scoped>
.page-shell {
  display: flex;
  flex-direction: column;
}

.btm-fixed {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #fafafa;
  z-index: 2;
}

.section-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
}

.detail-card {
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quote-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
}

.section-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.section-fields {
  flex: 1;
  display: grid;
  gap: 10px;
}

.section-name {
  margin: 2px 0 0;
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}

.section-description {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.4;
}

.section-actions-right {
  display: inline-flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.quote-table-shell {
  border-radius: 12px;
  overflow: hidden;
}

.quote-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.quote-table th,
.quote-table td {
  padding: 8px 7px;
  text-align: left;
}

.quote-table th {
  background: #f8fafc;
  color: #475569;
  font-size: var(--font-size-sm);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.quote-table td {
  color: #111827;
  font-size: 14px;
  border-bottom: 1px solid #eef2f6;
}

.quote-table tbody tr:last-child td {
  border-bottom: 1px solid #e2e8f0;
}

.quote-table tfoot td {
  padding-top: 9px;
  padding-bottom: 9px;
  border-bottom: 0;
  background: #f8fafc;
  font-size: var(--font-size-default);
}

.quote-col-item {
  width: 60%;
}

.quote-table th.quote-col-total,
.quote-table td.quote-col-total {
  width: 25%;
  min-width: 140px;
  text-align: right;
  white-space: nowrap;
}

.quote-col-actions {
  width: 15%;
  text-align: right;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.quote-empty {
  padding: 14px 12px;
  color: #64748b;
  font-size: 14px;
}

.section-total {
  font-weight: 700;
  color: #111827;
  text-align: right;
}

.section-total-spacer {
  width: 14%;
}

.section-total-label {
  font-weight: 600;
  color: #46505e;
  text-align: right;
}

.summary-table {
  display: flex;
  justify-content: flex-end;
  padding: 14px 0 0;
}

.summary {
  width: min(420px, 100%);
  border-collapse: collapse;
}

.summary td {
  padding: 4px 0;
}

.summary-table .section-total-label {
  font-weight: 400;
}

.summary-table .section-total {
  font-weight: 500;
}

.tax-row-label {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.tax-row-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.tax-chip {
  font-weight: 600;
  color: #111827;
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  padding: 2px 8px;
  border-radius: 999px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tax-line-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.tax-line-label {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
